import {memo} from 'react';
import {Check, Copy, PanelRightOpen, RotateCcw, StickyNote, Tag, X} from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useCopy
} from '@kroll-resolver-crisp/ui-components-react';
import {type Item} from '@/api/items';
import {type ImageMatchTier, imageMatchTierLabels} from '@/api/jobs';
import {type FlagColor} from '@/api/flags';
import {
  getSubmissionStatusTags,
  ItemQueueLeaseBadge,
  ChannelSubmittedBadge,
  SubmissionStatusTags,
  ThumbnailChangedBadge
} from '@/components/items/ItemBadges';
import {ChannelMenu} from '@/components/items/ChannelMenu';
import {ItemFlagPicker} from '@/components/items/ItemCard';
import {RdSignalsSection} from '@/components/items/RdSignalsSection';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {TablePanel} from '@/components/ui/table-panel';
import {ItemLanguage, ItemStats} from '@/components/items/ItemStats';
import {CopyUrlButton} from '@/components/shared/CopyableUrl';
import {SensitiveMedia} from '@/components/shared/SensitiveMedia';
import {useThumbnailLoadFailed} from '@/components/shared/Thumbnail';
import {getItemCardModel, VARIANT_LABEL} from '@/lib/itemCard';
import {getDeclaredLanguageSubtag} from '@/lib/itemMetadata';
import {ACTOR_NOUN, ITEM_NOUN} from '@/lib/platform';
import {worstStatusRowBorderClassName} from '@/lib/submissionStatusStyle';
import {itemPlatformId} from '@/lib/itemIds';
import {cn} from '@/lib/utils';
import {imageMatchIdFor} from '@/lib/imageMatchId';
import {useTranslatedText} from '@/store/translationStore';

export type ItemListViewProps = {
  readonly items: readonly Item[];
  // Shows every row's full description and tags under its title, so a page of results can be
  // read without opening each one.
  readonly detailed?: boolean;
  readonly selectedItemIds: ReadonlySet<string>;
  readonly onToggleCheck: (id: string) => void;
  // Select (true) or clear (false) every row on screen. Omit and the header checkbox is hidden.
  readonly onToggleAll?: (select: boolean) => void;
  readonly onClassify?: (item: Item) => void;
  readonly onClassifyChannel?: (item: Item) => void;
  readonly onSetFlag?: (item: Item, color: FlagColor | null) => void;
  readonly onDismiss?: (item: Item) => void;
  readonly onOpen?: (item: Item) => void;
  // Opens the caller's details sidebar on a row, as a card's Show more does.
  readonly onShowDetails?: (item: Item) => void;
  // Only provided for image_search jobs - the Match column appears with it.
  readonly matchTiers?: ReadonlyMap<string, ImageMatchTier>;
};

// The compact list: one row per item, the same data and actions as the card.
export function ItemListView({
  items,
  detailed = false,
  selectedItemIds,
  onToggleCheck,
  onToggleAll,
  onClassify,
  onClassifyChannel,
  onSetFlag,
  onDismiss,
  onOpen,
  onShowDetails,
  matchTiers
}: ItemListViewProps) {
  const selectedOnScreen = items.filter((i) => selectedItemIds.has(i.id)).length;
  const allSelected = items.length > 0 && selectedOnScreen === items.length;
  const showMatch = matchTiers !== undefined && matchTiers.size > 0;

  return (
    <TablePanel>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-6">
              {onToggleAll ? (
                <Checkbox
                  checked={allSelected}
                  indeterminate={selectedOnScreen > 0 && !allSelected}
                  aria-label="Select all items on this page"
                  onCheckedChange={() => {
                    onToggleAll(!allSelected);
                  }}
                />
              ) : (
                <span className="sr-only">Select</span>
              )}
            </TableHead>
            <TableHead className="w-6">
              <span className="sr-only">Flag</span>
            </TableHead>
            <TableHead>Title</TableHead>
            <TableHead>Platform</TableHead>
            <TableHead>Channel</TableHead>
            <TableHead>Type</TableHead>
            {showMatch ? <TableHead>Match</TableHead> : null}
            <TableHead>Status</TableHead>
            <TableHead>Details</TableHead>
            <TableHead className="w-48 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <ItemListRow
              key={item.id}
              item={item}
              isChecked={selectedItemIds.has(item.id)}
              showMatch={showMatch}
              detailed={detailed}
              matchTiers={matchTiers}
              onToggleCheck={onToggleCheck}
              onDismiss={onDismiss}
              onClassify={onClassify}
              onClassifyChannel={onClassifyChannel}
              onSetFlag={onSetFlag}
              onOpen={onOpen}
              onShowDetails={onShowDetails}
            />
          ))}
        </TableBody>
      </Table>
    </TablePanel>
  );
}

// Its own component (not inlined in ItemListView's .map) so useTranslatedText - a hook - can be
// called once per row at a stable position. Wrapped in memo like ItemCard, for the same reason:
// callbacks take the id/item instead of closing over it, so the list can hand every row the same
// stable callback.
/* eslint-disable react/require-default-props -- memo() hides the defaults from the rule */
const ItemListRow = memo(
  ({
    item,
    isChecked,
    showMatch,
    detailed,
    onToggleCheck,
    onDismiss,
    onClassify,
    onClassifyChannel,
    onSetFlag,
    onOpen,
    onShowDetails,
    matchTiers
  }: {
    readonly item: Item;
    readonly isChecked: boolean;
    readonly showMatch: boolean;
    readonly detailed: boolean;
    readonly onToggleCheck: (id: string) => void;
    readonly onDismiss?: ((item: Item) => void) | undefined;
    readonly onClassify?: ((item: Item) => void) | undefined;
    readonly onClassifyChannel?: ((item: Item) => void) | undefined;
    readonly onSetFlag?: ((item: Item, color: FlagColor | null) => void) | undefined;
    readonly onOpen?: ((item: Item) => void) | undefined;
    readonly onShowDetails?: ((item: Item) => void) | undefined;
    readonly matchTiers?: ReadonlyMap<string, ImageMatchTier> | undefined;
  }) => {
    // The card's own model, so a row names, dates and describes an item exactly as its card does.
    const model = getItemCardModel(item);
    const translatedTitle = useTranslatedText(item.id, model.name) ?? model.name;
    const translatedDescription = useTranslatedText(item.id, model.description) ?? '';
    const {copy: copyId, isCopied: idCopied} = useCopy({value: () => itemPlatformId(item)});
    const isChannel = model.variant === 'channel' || model.variant === 'profile';
    const dismissed = item.item_status === 'dismissed';
    const submissionStatusTags = getSubmissionStatusTags(item.submission_status_groups);
    const hasChannelStatus =
      !isChannel && getSubmissionStatusTags(item.channel_submission_status_groups ?? []).length > 0;
    const youtubeId = imageMatchIdFor(item);
    const matchTier = youtubeId ? matchTiers?.get(youtubeId) : undefined;
    const nounLabel = isChannel ? ACTOR_NOUN : ITEM_NOUN;
    const offersChannel = Boolean(onClassifyChannel && !isChannel && item.author_id);
    const avatarLoad = useThumbnailLoadFailed(item.thumbnail_url);

    return (
      <TableRow
        data-state={isChecked ? 'selected' : undefined}
        className={cn(
          'group',
          onOpen && 'cursor-pointer',
          worstStatusRowBorderClassName(submissionStatusTags.map((tag) => tag.status)),
          dismissed && 'opacity-50'
        )}
        onClick={
          onOpen
            ? () => {
                onOpen(item);
              }
            : undefined
        }>
        <TableCell
          onClick={(event) => {
            event.stopPropagation();
          }}>
          <Checkbox
            checked={isChecked}
            aria-label={`Select ${translatedTitle}`}
            onCheckedChange={() => {
              onToggleCheck(item.id);
            }}
          />
        </TableCell>
        <TableCell className="px-1">
          <ItemFlagPicker item={item} onSetFlag={onSetFlag} />
        </TableCell>
        <TableCell>
          <div className={cn('flex items-start gap-3', detailed ? 'max-w-2xl' : 'max-w-md')}>
            {isChannel && avatarLoad.failed ? (
              <Avatar aria-hidden="true" className="size-10">
                <AvatarFallback variant="primary">
                  {(item.author_handle ?? item.author_name ?? '?').charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            ) : item.thumbnail_url ? (
              isChannel ? (
                // The Avatar frame around the media rather than an AvatarImage, so the blur
                // preference still applies.
                <Avatar className="size-10 after:pointer-events-none">
                  <SensitiveMedia
                    compact
                    src={item.thumbnail_url}
                    storedSha256={item.stored_thumbnail_sha256}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="aspect-square size-full rounded-full object-cover"
                    onError={avatarLoad.onError}
                  />
                </Avatar>
              ) : (
                <div className="relative h-10 w-16 shrink-0">
                  <SensitiveMedia
                    compact
                    src={item.thumbnail_url}
                    storedSha256={item.stored_thumbnail_sha256}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="bg-muted border-border h-10 w-full rounded-md border object-cover"
                  />
                  {model.duration ? (
                    <Badge
                      variant="secondary"
                      className="absolute right-0.5 bottom-0.5 h-auto px-1 py-0 text-[10px] tabular-nums">
                      {model.duration}
                    </Badge>
                  ) : null}
                </div>
              )
            ) : null}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-foreground line-clamp-1 font-medium">
                  {translatedTitle ?? item.external_id}
                </p>
                <ItemQueueLeaseBadge item={item} />
                {item.thumbnail_changed ? <ThumbnailChangedBadge /> : null}
              </div>
              {item.flag_note ? (
                <p
                  className="bg-primary/10 text-foreground border-primary flex items-start gap-1.5 rounded-md border-l-2 px-2 py-1 text-xs"
                  title={item.flag_note}>
                  <StickyNote aria-hidden className="text-primary-ink mt-0.5 size-3 shrink-0" />
                  <span className="line-clamp-2">{item.flag_note}</span>
                </p>
              ) : null}
              {model.date ? (
                <p className="text-muted-foreground text-xs" title={model.date.title}>
                  {model.date.text}
                </p>
              ) : null}
              {detailed && translatedDescription ? (
                <p className="text-muted-foreground mt-1 text-xs leading-normal break-words whitespace-pre-line">
                  {translatedDescription}
                </p>
              ) : null}
              {detailed && model.tags.length > 0 ? (
                <div className="mt-1 flex flex-wrap gap-1">
                  {model.tags.map((tag) => (
                    <Badge key={tag} variant="tag">
                      {tag}
                    </Badge>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </TableCell>
        <TableCell>
          <Badge variant="outline">{item.platform}</Badge>
        </TableCell>
        <TableCell className="text-muted-foreground">
          {offersChannel && onClassifyChannel ? (
            <ChannelMenu item={item} onClassifyChannel={onClassifyChannel}>
              {item.author_handle ?? item.author_name ?? '—'}
            </ChannelMenu>
          ) : (
            (item.author_handle ?? item.author_name ?? '—')
          )}
        </TableCell>
        <TableCell>{VARIANT_LABEL[model.variant]}</TableCell>
        {showMatch ? (
          <TableCell>
            {matchTier ? <Badge variant="outline">{imageMatchTierLabels[matchTier]}</Badge> : '—'}
          </TableCell>
        ) : null}
        <TableCell>
          <div className="flex flex-wrap items-center gap-1">
            {item.thumbnail_changed ? <ThumbnailChangedBadge /> : null}
            {submissionStatusTags.length > 0 ? (
              <SubmissionStatusTags tags={submissionStatusTags} />
            ) : item.thumbnail_changed || hasChannelStatus ? null : (
              <span className="text-muted-foreground">—</span>
            )}
            {isChannel ? null : (
              <ChannelSubmittedBadge groups={item.channel_submission_status_groups} />
            )}
          </div>
        </TableCell>
        {/* The card's stats line, chip for chip, so both views read the same. */}
        <TableCell
          className="min-w-64 whitespace-normal"
          onClick={(event) => {
            // Translate is a control of its own - it must not also open the row.
            event.stopPropagation();
          }}>
          <ItemStats
            stats={model.stats}
            trailing={
              <ItemLanguage
                itemKey={item.id}
                language={model.language}
                texts={[model.name, model.description, ...model.tags]}
                offerTranslate={getDeclaredLanguageSubtag(item.metadata) !== 'en'}
              />
            }
          />
          <RdSignalsSection itemId={item.id} assessment={item.rd_assessment} />
        </TableCell>
        <TableCell
          className="text-right"
          onClick={(event) => {
            event.stopPropagation();
          }}>
          <div className="flex items-center justify-end gap-1">
            {onDismiss ? (
              <Button
                variant="secondary"
                size="xs"
                onClick={() => {
                  onDismiss(item);
                }}>
                {dismissed ? <RotateCcw /> : <X />}
                {dismissed ? 'Restore' : 'Dismiss'}
              </Button>
            ) : null}
            {onClassify ? (
              <Button
                size="xs"
                onClick={() => {
                  onClassify(item);
                }}>
                <Tag />
                Classify
              </Button>
            ) : null}
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Copy ${nounLabel} ID`}
                    onClick={() => {
                      void copyId();
                    }}
                  />
                }>
                {idCopied ? <Check /> : <Copy />}
              </TooltipTrigger>
              <TooltipContent>{idCopied ? 'Copied' : `Copy ${nounLabel} ID`}</TooltipContent>
            </Tooltip>
            {onShowDetails ? (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label="Show more"
                      aria-haspopup="dialog"
                      onClick={() => {
                        onShowDetails(item);
                      }}
                    />
                  }>
                  <PanelRightOpen />
                </TooltipTrigger>
                <TooltipContent>Show more</TooltipContent>
              </Tooltip>
            ) : null}
            {item.url ? <CopyUrlButton url={item.url} label={`Copy ${nounLabel} URL`} /> : null}
          </div>
        </TableCell>
      </TableRow>
    );
  }
);
/* eslint-enable react/require-default-props */
